import { Link, useLocation, useRouteError } from 'react-router'
import { useCallback, useEffect, useState } from 'react'
import { usePostHog } from 'posthog-js/react'
import { useMutation } from '@tanstack/react-query'
import { Button } from '@/components/ui'
import { axiosInstance } from '@/api/axios'
import { useAuthStore } from '@/feature/auth/store/authStore'

const reportError = async (msg: string) => {
  await axiosInstance.post(`/user/log-issue`, {
    message: msg
  })
}

export const GlobalError = () => {
  const error = useRouteError()
  const posthog = usePostHog()
  const [feedback, setFeedback] = useState(``)
  const { user } = useAuthStore()

  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: () => reportError(feedback)
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Here you could send feedback to your backend or analytics
    mutate()
  }

  useEffect(() => {
    if (error) {
      console.error(`error`, error)
      if (posthog?.captureException && posthog.config.capture_exceptions) {
        posthog.captureException(error)
      } else {
        console.warn(`Capture exceptions is disabled`)
      }
    }
  }, [error, posthog])

  if (isSuccess) {
    return (
      <div className='flex h-screen flex-col items-center justify-center'>
        <h1 className='mb-4 text-xl font-medium text-gray-700'>Thank you for your feedback!</h1>
        <Link to={`/dashboard`}>
          <Button>Go back to the home page</Button>
        </Link>
      </div>
    )
  }
  if (!user?.email) {
    return (
      <div className='flex h-screen flex-col items-center justify-center bg-white'>
        <h1 className='mb-2 text-3xl font-medium text-gray-700'>Refresh the page to try again</h1>
        <p className='mb-6 text-gray-600'>If that does not work, please return to the login page and try again.</p>
        <Link to={`/login`}>
          <Button>Go to login page</Button>
        </Link>
      </div>
    )
  }
  return (
    <div className='flex h-screen flex-col items-center justify-center bg-white'>
      <div className='flex w-full max-w-2xl flex-col rounded-lg border p-8 shadow-sm'>
        <h1 className='mb-2 text-3xl font-medium text-gray-700'>Refresh the page to try again</h1>
        <p className='mb-6 text-gray-600'>If that does not work, please tell us what you were trying to do so we can address the issue.</p>
        <form onSubmit={handleSubmit} className='flex w-full flex-col'>
          <textarea
            className='mb-4 h-32 w-full resize-none rounded border p-3 font-mono'
            placeholder='Open the Web Analytics sections, and app crashes'
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
          />
          <div className='flex justify-end'>
            <Button type='submit' disabled={isPending}>
              {isPending ? `Submitting...` : `Submit`}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export const ManualErrorRoute = () => {
  const location = useLocation()

  const handleError = useCallback(() => {
    // Instead of navigating, we'll throw the error and let React handle it
    throw new Error(`This is a manual error`)
  }, [])

  useEffect(() => {
    // This ensures the error is thrown during React's lifecycle
    if (location.pathname === `/manual-error`) {
      handleError()
    }
  }, [handleError, location.pathname])

  return (
    <div className='flex h-screen flex-col items-center justify-center'>
      <h1>Destroy the world</h1>
      <Button onClick={handleError}>Destroy the world</Button>
    </div>
  )
}
